import { D, st, toArr, calcTotal } from '../constants';
import { InvoiceBadge } from './shared';

export default function Dashboard({bids, estimates, invoices, clients, payments, deposits, fmt, fmtDate, clientName, setPage, openInvoiceModal, openInvoiceList, setNavigateTo}) {
  const today = new Date().toISOString().slice(0,10);
  const thisMonth = today.slice(0,7);

  const paidFor = inv => toArr(payments).filter(p=>p.invoice_id===inv.id).reduce((s,p)=>s+(p.amount||0),0);

  const unpaidInvoices = toArr(invoices).filter(i=>i.status!=="Paid");
  const outstandingTotal = unpaidInvoices.reduce((s,i)=>s+(calcTotal(i.items)-paidFor(i)),0);
  const overdueInvoices = unpaidInvoices.filter(i=>i.dueDate && i.dueDate < today);
  const overdueTotal = overdueInvoices.reduce((s,i)=>s+(calcTotal(i.items)-paidFor(i)),0);

  const paidThisMonth = toArr(payments).filter(p=>(p.date||"").slice(0,7)===thisMonth).reduce((s,p)=>s+(p.amount||0),0);

  const pendingBids = toArr(bids).filter(b=>b.status==="Pending");
  const sentEstimates = toArr(estimates).filter(e=>e.status==="Sent");
  const approvedEstimates = toArr(estimates).filter(e=>e.status==="Approved");

  const cards = [
    {label:"Outstanding", value:fmt(outstandingTotal), sub:`${unpaidInvoices.length} unpaid invoice${unpaidInvoices.length===1?"":"s"}`, color:D.accent, onClick:()=>setPage("invoices")},
    {label:"Overdue", value:fmt(overdueTotal), sub:`${overdueInvoices.length} invoice${overdueInvoices.length===1?"":"s"} past due`, color:overdueInvoices.length>0?D.danger:D.muted, onClick:()=>setPage("invoices")},
    {label:"Paid this month", value:fmt(paidThisMonth), sub:"Payments received", color:D.success, onClick:()=>setPage("invoices")},
    {label:"Pending Bids", value:pendingBids.length, sub:"Awaiting decision", color:D.accent, onClick:()=>setPage("bids")},
    {label:"Sent Estimates", value:sentEstimates.length, sub:"Awaiting approval", color:D.warning, onClick:()=>setPage("bids")},
    {label:"Ready to Invoice", value:approvedEstimates.length, sub:"Approved estimates", color:D.success, onClick:()=>setPage("bids")},
  ];

  const activity = [
    ...toArr(bids).map(b=>({type:"Bid", date:b.date, label:`${b.number} — ${clientName(b.clientId)}`, amount:0, status:b.status, page:"bids", id:b.id, itemType:"bid"})),
    ...toArr(invoices).map(i=>({type:"Invoice", date:i.date, label:`${i.number} — ${clientName(i.clientId)}`, amount:calcTotal(i.items), status:i.status, page:"invoices"})),
    ...toArr(estimates).map(e=>({type:"Estimate", date:e.date, label:`${e.number} — ${clientName(e.clientId)}`, amount:calcTotal(e.items), status:e.status, page:"bids", id:e.id, itemType:"estimate"})),
    ...toArr(payments).map(p=>({type:"Payment", date:p.date, label:`Payment from ${clientName(p.client_id)}`, amount:p.amount, status:"Received", page:"payments"})),
    ...toArr(deposits).map(d=>({type:"Deposit", date:d.date, label:`Deposit from ${clientName(d.client_id)}`, amount:d.amount, status:"Received", page:"deposits"})),
  ].filter(a=>a.date).sort((a,b)=>b.date.localeCompare(a.date)).slice(0,10);

  const typeColors = {Bid:D.muted, Invoice:D.accent, Estimate:D.warning, Payment:D.success, Deposit:D.success};

  return (
    <div>
      <div style={{...st.h1, marginBottom:20}}>Dashboard</div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(160px,1fr))",gap:14,marginBottom:24}}>
        {cards.map(c=>(
          <div key={c.label} style={{...st.card, cursor:"pointer"}} onClick={c.onClick}>
            <div style={{fontSize:11,color:D.muted,marginBottom:6}}>{c.label}</div>
            <div style={{fontSize:22,fontWeight:600,color:c.color,marginBottom:4}}>{c.value}</div>
            <div style={{fontSize:11,color:D.muted}}>{c.sub}</div>
          </div>
        ))}
      </div>

      <div style={{display:"flex",gap:10,marginBottom:24,flexWrap:"wrap"}}>
        <button style={st.btn("primary")} onClick={()=>setPage("bids")}>+ New Bid</button>
        <button style={st.btn("primary")} onClick={()=>openInvoiceModal ? openInvoiceModal(null) : setPage("invoices")}>+ New Invoice</button>
        <button style={st.btn()} onClick={()=>setPage("clients")}>+ New Client</button>
      </div>

      <div style={st.card}>
        <div style={{fontWeight:600, marginBottom:12}}>Recent Activity</div>
        {activity.length===0 && <div style={{color:D.muted,fontSize:13}}>No activity yet.</div>}
        {activity.length>0 && (
          <div style={{overflowX:"auto"}}><table style={st.table}>
            <thead><tr>{["Date","Type","Description","Amount","Status"].map(h=><th key={h} style={st.th}>{h}</th>)}</tr></thead>
            <tbody>
              {activity.map((a,idx)=>(
                <tr key={idx} style={{cursor:"pointer"}} onClick={()=>{if(a.itemType&&a.id&&setNavigateTo)setNavigateTo({id:a.id,type:a.itemType});setPage(a.page||"dashboard");}}>
                  <td style={st.td}>{fmtDate(a.date)}</td>
                  <td style={st.td}><span style={{color:typeColors[a.type]||D.muted,fontWeight:600,fontSize:12}}>{a.type}</span></td>
                  <td style={st.td}>{a.label}</td>
                  <td style={st.td}>{fmt(a.amount)}</td>
                  <td style={st.td}>{a.type==="Invoice" ? <InvoiceBadge status={a.status}/> : <span style={{fontSize:12,color:D.muted}}>{a.status}</span>}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </div>
  );
}
